import React, { useEffect } from 'react';
import UploadInformationSubArea from './UploadInformationSubArea';
import styled from 'styled-components';
import { useMappedState } from 'redux-react-hook';
import { getLocalizedText as t } from '@vp-dst/localization-util';

import { Typography, Column, Row, GridContainer, Tabs, Tab } from '@vp/react-vp-ui-library';
import { State } from '../state/reducer';
import { selectProducts } from '../state/selectors';
import { Product } from '../services/products';
import { getFormattedSize, Format } from '../utilities/size';
import SpecsRectangle from './SpecsRectangle';
import DownloadableTemplates from './DownloadableTemplates';
import { ProductSpecs } from '../services/specs';

const SpecificationAreaWrapper = styled.div`
  margin-top: 40px;
`;

const BleedLine = styled.div`
  width: 50px;
  border-bottom: 3px solid #ee0285;
  margin-top: 15px;
  margin-bottom: 10px;
`;

const TrimLine = styled.div`
  width: 50px;
  border-bottom: 3px solid #6ecff5;
  margin-top: 15px;
  margin-bottom: 10px;
`;

const SafeLine = styled.div`
  width: 50px;
  border-bottom: 3px dashed #919699;
  margin-top: 15px;
  margin-bottom: 10px;
`;

function determineScaleFactor(
  productSpecsBySKU: { [sku: string]: ProductSpecs },
  maxHeight: number,
  maxWidth: number,
) {
  const maxProductHeight = Math.max(
    ...Object.values(productSpecsBySKU).map(specs => specs.bleedCentimeters.height),
  );
  const maxProductWidth = Math.max(
    ...Object.values(productSpecsBySKU).map(specs => specs.bleedCentimeters.width),
  );

  const widthScale = maxWidth / maxProductWidth;
  const heightScale = maxHeight / maxProductHeight;

  return Math.min(widthScale, heightScale);
}

const mapState = (state: State) => {
  return {
    products: selectProducts(state),
    // loadableProductSpecsBySKU is guaranteed to be loaded when this renders, so we can use the non-null assertion (!)
    productSpecsBySKU: state.loadableProductSpecsBySKU!.data as { [sku: string]: ProductSpecs },
  };
};

function SpecificationArea() {
  useEffect(() => {
    // the ui library triggers initializeControl and accordionize on load
    // we need to trigger them ourselves because our content is not guaranteed to exist before the load event fires
    // @ts-ignore
    $('#specs-group-tabs').trigger('initializeControl');
    // @ts-ignore
    $('#specs-group-tabs').trigger('accordionizeTab');
  }, []);

  const { products, productSpecsBySKU } = useMappedState(mapState);

  const specsGroupings: { [specsGrouping: string]: Product[] } = products.reduce<{
    [specsGroup: string]: Product[];
  }>((groupings: { [specsGrouping: string]: Product[] }, product: Product) => {
    if (Array.isArray(groupings[product.specsGrouping])) {
      groupings[product.specsGrouping].push(product);
    } else {
      groupings[product.specsGrouping] = [product];
    }

    return groupings;
  }, {});

  // all specs rectangles should have the same scale
  // we use `determineScaleFactor` to calculate the scaleFactors for every shape
  // then we use the minimum of those scaleFactors for every shape
  const specsRectanglesMaxHeight = 300;
  const specsRectanglesMaxWidth = 300;
  const specsRectanglesScaleFactor = determineScaleFactor(
    productSpecsBySKU,
    specsRectanglesMaxHeight,
    specsRectanglesMaxWidth,
  );

  return (
    <SpecificationAreaWrapper>
      <GridContainer>
        <Row>
          <Column span={12}>
            <div style={{ textAlign: 'center', marginBottom: 50 }}>
              <Typography size={2} strong={true} purpose="heading">
                {t('upload-specifications')}
              </Typography>
            </div>
          </Column>
        </Row>
        <Row>
          <Column span={12}>
            <div
              style={{ marginBottom: 5 }}
              id="specs-group-tabs"
              className="tabs tabs-skin-standard tabs-center-headers"
            >
              <Tabs>
                {Object.entries(specsGroupings).map(([specsGroup, products], index) => {
                  const product = products[0];

                  return (
                    <Tab heading={specsGroup}>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                        }}
                      >
                        <GridContainer>
                          <Row>
                            <div
                              style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}
                            >
                              <Column span={4}>
                                <div
                                  style={{
                                    height: 300,
                                    width: '100%',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                  }}
                                >
                                  <SpecsRectangle
                                    scaleFactor={specsRectanglesScaleFactor}
                                    labelComponent={<Typography>{t('safe-area')}</Typography>}
                                    boundaries={[
                                      {
                                        height: product.specs.bleedCentimeters.height,
                                        width: product.specs.bleedCentimeters.width,
                                        offsetX: product.specs.bleedCentimeters.anchorX,
                                        offsetY: product.specs.bleedCentimeters.anchorY,
                                        cornerRadius: product.specs.cornerRadiusCentimeters,
                                        borderWidth: 1,
                                        borderStyle: 'solid',
                                        borderColor: '#ee0285',
                                        backgroundStyle: 'rgba(238, 2, 133, 0.065)', // same color as border, but with alpha
                                      },
                                      {
                                        height: product.specs.trimCentimeters.height,
                                        width: product.specs.trimCentimeters.width,
                                        offsetX: product.specs.trimCentimeters.anchorX,
                                        offsetY: product.specs.trimCentimeters.anchorY,
                                        cornerRadius: product.specs.cornerRadiusCentimeters,
                                        borderWidth: 1,
                                        borderStyle: 'solid',
                                        borderColor: '#6ecff5',
                                        backgroundStyle: 'white',
                                      },
                                      {
                                        height: product.specs.safeAreaCentimeters.height,
                                        width: product.specs.safeAreaCentimeters.width,
                                        offsetX: product.specs.safeAreaCentimeters.anchorX,
                                        offsetY: product.specs.safeAreaCentimeters.anchorY,
                                        cornerRadius: product.specs.cornerRadiusCentimeters,
                                        borderWidth: 1,
                                        borderStyle: 'dashed',
                                        borderColor: '#919699',
                                        backgroundStyle: 'white',
                                      },
                                    ]}
                                  />
                                </div>
                              </Column>
                              <Column className="col-offset-1" span={2}>
                                <BleedLine />
                                <Typography
                                  purpose="heading"
                                  size={5}
                                  strong={true}
                                  style={{ marginBottom: 20 }}
                                >
                                  {t('bleed')}
                                </Typography>
                                <Typography style={{ marginTop: 0, marginBottom: 10 }}>
                                  {getFormattedSize(
                                    product.specs.bleedCentimeters.width,
                                    product.specs.bleedCentimeters.height,
                                    Format.MM,
                                  )}
                                </Typography>
                                <Typography style={{ marginTop: 0, marginBottom: 10 }}>
                                  {getFormattedSize(
                                    product.specs.bleedCentimeters.width,
                                    product.specs.bleedCentimeters.height,
                                    Format.IN,
                                  )}
                                </Typography>
                                <Typography style={{ marginTop: 0, marginBottom: 15 }}>
                                  {getFormattedSize(
                                    product.specs.bleedCentimeters.width,
                                    product.specs.bleedCentimeters.height,
                                    Format.PX300DPI,
                                  )}
                                </Typography>
                              </Column>
                              <Column span={2}>
                                <TrimLine />
                                <Typography
                                  purpose="heading"
                                  size={5}
                                  strong={true}
                                  style={{ marginBottom: 20 }}
                                >
                                  {t('trim')}
                                </Typography>
                                <Typography style={{ marginTop: 0, marginBottom: 10 }}>
                                  {getFormattedSize(
                                    product.specs.trimCentimeters.width,
                                    product.specs.trimCentimeters.height,
                                    Format.MM,
                                  )}
                                </Typography>
                                <Typography style={{ marginTop: 0, marginBottom: 10 }}>
                                  {getFormattedSize(
                                    product.specs.trimCentimeters.width,
                                    product.specs.trimCentimeters.height,
                                    Format.IN,
                                  )}
                                </Typography>
                                <Typography style={{ marginTop: 0, marginBottom: 15 }}>
                                  {getFormattedSize(
                                    product.specs.trimCentimeters.width,
                                    product.specs.trimCentimeters.height,
                                    Format.PX300DPI,
                                  )}
                                </Typography>
                              </Column>
                              <Column span={2}>
                                <SafeLine />
                                <Typography
                                  purpose="heading"
                                  size={5}
                                  strong={true}
                                  style={{ marginBottom: 20 }}
                                >
                                  {t('safe-area')}
                                </Typography>
                                <Typography style={{ marginTop: 0, marginBottom: 10 }}>
                                  {getFormattedSize(
                                    product.specs.safeAreaCentimeters.width,
                                    product.specs.safeAreaCentimeters.height,
                                    Format.MM,
                                  )}
                                </Typography>
                                <Typography style={{ marginTop: 0, marginBottom: 10 }}>
                                  {getFormattedSize(
                                    product.specs.safeAreaCentimeters.width,
                                    product.specs.safeAreaCentimeters.height,
                                    Format.IN,
                                  )}
                                </Typography>
                                <Typography style={{ marginTop: 0, marginBottom: 15 }}>
                                  {getFormattedSize(
                                    product.specs.safeAreaCentimeters.width,
                                    product.specs.safeAreaCentimeters.height,
                                    Format.PX300DPI,
                                  )}
                                </Typography>
                              </Column>
                            </div>
                          </Row>
                        </GridContainer>
                      </div>
                      <div
                        style={{
                          borderTop: '1px solid #c8cbcc',
                          borderBottom: '1px solid #c8cbcc',
                        }}
                      >
                        <UploadInformationSubArea />
                      </div>
                      <DownloadableTemplates specsGroup={specsGroup} products={products} />
                    </Tab>
                  );
                })}
              </Tabs>
            </div>
          </Column>
        </Row>
      </GridContainer>
    </SpecificationAreaWrapper>
  );
}

export default SpecificationArea;
